import * as yup from 'yup';
import YupPassword from 'yup-password';
import {
  PASSWORD_INCLUDE_LOWERCASE,
  PASSWORD_INCLUDE_NUMBERS,
  PASSWORD_INCLUDE_SYMBOLS,
  PASSWORD_INCLUDE_UPPERCASE,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
} from '../../../utils/constants';

// Extend yup with yup-password
YupPassword(yup);

/**
 * Gets custom password rules based on config
 */
const getPasswordRules = () => {
  return yup.string()
    .required('Please set a password for your account.')
    .min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`)
    .when([], {
      is: () => PASSWORD_MAX_LENGTH > 0 && PASSWORD_MAX_LENGTH > PASSWORD_MIN_LENGTH,
      then: (field) => field.max(PASSWORD_MAX_LENGTH, `Password must be at most ${PASSWORD_MAX_LENGTH} characters.`),
    })
    .when([], {
      is: () => PASSWORD_INCLUDE_LOWERCASE,
      then: (field) => field.minLowercase(1, 'Password must contain at least 1 lowercase letter.'),
    })
    .when([], {
      is: () => PASSWORD_INCLUDE_UPPERCASE,
      then: (field) => field.minUppercase(1, 'Password must contain at least 1 uppercase letter.'),
    })
    .when([], {
      is: () => PASSWORD_INCLUDE_NUMBERS,
      then: (field) => field.minNumbers(1, 'Password must contain at least 1 number.'),
    })
    .when([], {
      is: () => PASSWORD_INCLUDE_SYMBOLS,
      then: (field) => field.minSymbols(1, 'Password must contain at least 1 symbol.'),
    });
};

const UserRegistrationValidation = yup.object()
  .shape({
    first_name: yup.string()
      .required('First name is required.'),
    last_name: yup.string()
      .required('Last name is required.'),
    email: yup.string()
      .email()
      .required('A valid email address is required.'),
    password: getPasswordRules(),
    role: yup.string()
      .required('You must select one of the above options'),
  });

const SellerVerificationMethod = yup.object()
  .shape({
    method: yup.string()
      .required('You must select one of the above options')
      .oneOf(['sms', 'call'], 'You must select Call or SMS'),
    changePhone: yup.bool(),
    phone: yup.string()
      .when(['changePhone'], {
        is: (changePhone) => changePhone,
        then: yup.string()
          .required('A phone number is required.'),
        otherwise: yup.string()
          .nullable(),
      }),
  });

const SellerSecureValidation = yup.object()
  .shape({
    token: yup.string()
      .required('Verification Code is required.'),
  });

const SellerDetailsValidation = yup.object()
  .shape({
    name: yup.string()
      .required('Breeder name is required.'),
    address_line_1: yup.string()
      .required('Address is required.'),
    city: yup.string()
      .required('City is required'),
    state: yup.string()
      .required('State is required.'),
    zip: yup.string()
      .required('Zip is required.')
      .matches(/^[0-9]+$/, 'Must be only digits')
      .min(5, 'Must be exactly 5 digits')
      .max(5, 'Must be exactly 5 digits'),
    contacts: yup.array().of(
      yup.object().shape({
        contact_preference: yup
          .array()
          .required('You must select one of the above options.')
          .min(1, 'You must select at least one of the above options.')
          .max(3, "You can select only: 'call', 'text', or 'email'")
          .of(yup.string().oneOf(['call', 'text', 'email'])),
      }),
    ),
  });

const TFAFirstStepValidation = yup.object()
  .shape({
    phone: yup.string()
      .required('Please enter a valid phone number.'),
  });

export {
  UserRegistrationValidation,
  SellerSecureValidation,
  SellerVerificationMethod,
  SellerDetailsValidation,
  TFAFirstStepValidation,
};
